/**
 * /confirm-pay — 确认订单 + 支付
 *
 * 设计稿: _design_reference/mini-confirm-pay.html
 *
 * 流程:
 * 1. 从 ?items=id1,id2 读取选中购物车项目
 * 2. useAddresses → 取默认地址显示
 * 3. useCart → 过滤出选中 items 展示商品回顾
 * 4. usePreOrder → 权威预订单金额(小计/国内运费/合单优惠)
 * 5. 买手备注输入
 * 6. 深色实付明细
 * 7. 立即支付 → useSubmitOrder → 拿到 orderNo → 展示 MiniCashier 收银台
 */
import {
  useAddresses,
  useCart,
  usePreOrder,
  useSubmitOrder,
} from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MiniCashier } from "../components/pay/MiniCashier";
import { TgHeader } from "../components/TgHeader";
import { TgMain } from "../components/TgMain";
import { TgPage } from "../components/TgPage";
import { TgSkeleton } from "../components/TgSkeleton";
import { useAuthToken } from "../lib/auth";

// ---------------------------------------------------------------------------
// Helper: read ?items= from URL search string
// ---------------------------------------------------------------------------
function getParam(search: string, key: string): string {
  const u = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return u.get(key) ?? "";
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------
export function ConfirmPayPage() {
  const { t: tOrder } = useTranslation("confirm_order");
  const { t: tPay } = useTranslation("pay_choose");
  const authToken = useAuthToken();
  const navigate = useNavigate();

  // Read selected item IDs from URL param
  const searchStr = useRouterState({ select: (s) => s.location.searchStr ?? "" });
  const rawItems = getParam(searchStr, "items");
  const selectedIds = useMemo(() => {
    if (!rawItems) return [] as string[];
    return rawItems
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }, [rawItems]);

  // Data hooks
  const addressesQ = useAddresses(authToken);
  const cartQ = useCart(authToken);
  const preOrderQ = usePreOrder(authToken, selectedIds);
  const submitMutation = useSubmitOrder(authToken);

  // UI state
  const [noteText, setNoteText] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  // After successful order creation, switch to cashier view
  const [orderNo, setOrderNo] = useState<string | null>(null);

  // Default address
  const defaultAddress = useMemo(
    () => addressesQ.data?.find((a) => a.isDefault) ?? addressesQ.data?.[0] ?? null,
    [addressesQ.data],
  );

  // Filter cart to selected items
  const selectedItems = useMemo(() => {
    if (!cartQ.data) return [];
    const idSet = new Set(selectedIds);
    return cartQ.data.platforms
      .flatMap((p) => p.shops.flatMap((s) => s.items))
      .filter((it) => idSet.has(it.id));
  }, [cartQ.data, selectedIds]);

  // PreOrder data
  const preOrder = preOrderQ.data;

  // Item count and shop count for recap header
  const { totalItemCount, shopCount } = useMemo(() => {
    if (!cartQ.data || selectedIds.length === 0)
      return { totalItemCount: selectedItems.length, shopCount: 1 };
    const idSet = new Set(selectedIds);
    const shops = new Set(
      cartQ.data.platforms
        .flatMap((p) => p.shops)
        .filter((s) => s.items.some((it) => idSet.has(it.id)))
        .map((s) => s.shopCode ?? s.shopName ?? "?"),
    );
    return {
      totalItemCount: selectedItems.reduce((acc, it) => acc + it.quantity, 0),
      shopCount: shops.size,
    };
  }, [cartQ.data, selectedIds, selectedItems]);

  // ---------------------------------------------------------------------------
  // If we have an orderNo, show the cashier view
  // ---------------------------------------------------------------------------
  if (orderNo && authToken) {
    return (
      <TgPage tabbar={false}>
        <TgHeader title={tPay("head.title")} back />
        <div className="tg-body" style={{ flex: 1 }}>
          {/* Dark receipt summary */}
          <div
            className="tg-card"
            style={{
              background: "linear-gradient(135deg,var(--color-text),#1A2924)",
              color: "#fff",
              border: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                paddingBottom: 8,
                marginBottom: 6,
                borderBottom: "2px solid var(--color-sticker-yellow)",
              }}
            >
              <div>
                <div
                  style={{ fontSize: 10, color: "rgba(255,255,255,.65)" }}
                >
                  {tPay("summary.amount_label")}
                </div>
                <div
                  style={{ fontSize: 9, color: "rgba(255,255,255,.5)", fontFamily: "monospace" }}
                >
                  {tPay("summary.order_no", { orderNo })}
                </div>
              </div>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 22,
                  fontWeight: 900,
                  color: "var(--color-sticker-yellow)",
                }}
              >
                {preOrder
                  ? formatPrice(preOrder.totalFeeRubKopecks, "ru-RU", "RUB")
                  : "—"}
              </div>
            </div>
          </div>

          <MiniCashier orderNo={orderNo} authToken={authToken} />
        </div>
      </TgPage>
    );
  }

  // ---------------------------------------------------------------------------
  // Submit order handler
  // ---------------------------------------------------------------------------
  async function handleSubmit() {
    setSubmitError(null);

    if (!authToken) {
      navigate({ to: "/login" as never });
      return;
    }

    if (!defaultAddress) {
      setSubmitError(tOrder("summary.error_no_address"));
      return;
    }

    if (selectedIds.length === 0) {
      return;
    }

    try {
      const result = await submitMutation.mutateAsync({
        addressId: defaultAddress.id,
        cartItems: selectedIds.map((id) => ({ id })),
        ...(noteText.trim().length > 0 ? { remarks: noteText.trim() } : {}),
      });
      setOrderNo(result);
    } catch (_e) {
      setSubmitError(tOrder("summary.error_generic"));
    }
  }

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------
  const isLoading =
    addressesQ.isLoading ||
    cartQ.isLoading ||
    (selectedIds.length > 0 && preOrderQ.isLoading);

  if (isLoading) {
    return (
      <TgPage tabbar={false}>
        <TgHeader title={tOrder("head.title")} back />
        <div className="tg-body" style={{ flex: 1 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "0 4px" }}>
            <TgSkeleton height={64} radius={12} />
            <TgSkeleton height={120} radius={10} />
            <TgSkeleton height={60} radius={10} />
            <TgSkeleton height={100} radius={10} />
          </div>
        </div>
      </TgPage>
    );
  }

  // ---------------------------------------------------------------------------
  // Grand total from preOrder (authoritative)
  // ---------------------------------------------------------------------------
  const totalFeeKopecks = preOrder?.totalFeeRubKopecks ?? 0;
  const expressFeeKopecks = preOrder?.totalExpressFeeRubKopecks ?? 0;
  const discountFeeKopecks = preOrder?.discountFeeRubKopecks ?? 0;
  // Subtotal = items only (total - express + discount)
  const itemsSubtotalKopecks = totalFeeKopecks - expressFeeKopecks + discountFeeKopecks;

  const previewItems = selectedItems.slice(0, 3);
  const hiddenCount = totalItemCount - previewItems.reduce((acc, it) => acc + it.quantity, 0);

  // ---------------------------------------------------------------------------
  // Render: confirm order view
  // ---------------------------------------------------------------------------
  return (
    <TgPage tabbar={false}>
      <TgHeader title={tOrder("head.title")} back />

      <div className="tg-body" style={{ flex: 1 }}>
        {/* Address block */}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate({ to: "/address" as never });
          }}
          style={{
            background: "linear-gradient(180deg,var(--color-primary-soft),#fff)",
            border: "1.5px solid var(--color-primary-fill)",
            borderRadius: 12,
            padding: "12px 14px",
            display: "grid",
            gridTemplateColumns: "36px 1fr auto",
            gap: 10,
            alignItems: "center",
            textDecoration: "none",
            color: "inherit",
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "var(--color-primary-fill)",
              color: "#fff",
              display: "grid",
              placeItems: "center",
              fontSize: 16,
            }}
          >
            📍
          </div>
          <div>
            {defaultAddress ? (
              <>
                <div style={{ fontSize: 12, fontWeight: 800 }}>
                  {defaultAddress.recipientName} · {defaultAddress.mobile}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: "var(--color-text-muted)",
                    marginTop: 2,
                    lineHeight: 1.4,
                  }}
                >
                  {defaultAddress.fullAddress}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-muted)" }}>
                {tOrder("address.add")}
              </div>
            )}
          </div>
          <span style={{ color: "var(--color-primary-fill)", fontSize: 14 }}>›</span>
        </a>

        {/* Items recap */}
        <div className="tg-card" style={{ padding: 0, overflow: "hidden" }}>
          <div
            style={{
              padding: "10px 12px",
              background: "var(--color-surface)",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {tOrder("items.title", { count: totalItemCount, shops: shopCount })}
          </div>
          {previewItems.map((it, i) => (
            <div
              key={it.id}
              style={{
                padding: "8px 12px",
                display: "grid",
                gridTemplateColumns: "32px 1fr auto",
                gap: 8,
                alignItems: "center",
                borderTop: "1px solid var(--color-border)",
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  background: "linear-gradient(135deg,#FFE5DD,#FFD3C2)",
                  display: "grid",
                  placeItems: "center",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                {it.imageUrl ? (
                  <img
                    src={it.imageUrl}
                    alt={it.title}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <span style={{ fontSize: 14 }}>🛍️</span>
                )}
              </div>
              <div style={{ fontSize: 11 }}>
                {it.title}
                {it.quantity > 1
                  ? ` × ${it.quantity}`
                  : ""}
              </div>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 12,
                  fontWeight: 800,
                  color: "var(--color-price)",
                  flexShrink: 0,
                }}
              >
                {formatPrice(it.priceRubKopecks * it.quantity, "ru-RU", "RUB")}
              </div>
            </div>
          ))}
          {hiddenCount > 0 && (
            <div
              style={{
                padding: "8px 12px",
                textAlign: "center",
                color: "var(--color-text-muted)",
                fontSize: 10,
                borderTop: "1px dashed var(--color-border)",
              }}
            >
              + {hiddenCount} {tOrder("items.view_more", { count: hiddenCount })}
            </div>
          )}
        </div>

        {/* Buyer note */}
        <div className="tg-card">
          <h4 className="tg-card__h">
            {tOrder("buyer_note.title")}
            <span style={{ fontWeight: 400, color: "var(--color-text-muted)" }}>
              {" "}
              {tOrder("buyer_note.optional")}
            </span>
          </h4>
          <input
            style={{
              width: "100%",
              border: "1px solid var(--color-border)",
              borderRadius: 8,
              padding: "8px 10px",
              fontSize: 11,
              background: "#fff",
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
            placeholder={tOrder("buyer_note.textarea_placeholder")}
            value={noteText}
            maxLength={200}
            onChange={(e) => setNoteText(e.target.value)}
          />
        </div>

        {/* Receipt — dark card */}
        <div
          className="tg-card"
          style={{
            background: "linear-gradient(135deg,var(--color-text),#1A2924)",
            color: "#fff",
            border: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 11,
              padding: "4px 0",
              color: "rgba(255,255,255,.85)",
            }}
          >
            <span>{tOrder("summary.items_count", { count: totalItemCount })}</span>
            <span>
              {preOrder
                ? formatPrice(itemsSubtotalKopecks, "ru-RU", "RUB")
                : "—"}
            </span>
          </div>
          {expressFeeKopecks > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                padding: "4px 0",
                color: "rgba(255,255,255,.85)",
              }}
            >
              <span>{tOrder("summary.domestic_fee")}</span>
              <span>{formatPrice(expressFeeKopecks, "ru-RU", "RUB")}</span>
            </div>
          )}
          {discountFeeKopecks > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                padding: "4px 0",
                color: "var(--color-primary)",
              }}
            >
              <span>{tOrder("summary.merge_discount", { shops: shopCount })}</span>
              <span>− {formatPrice(discountFeeKopecks, "ru-RU", "RUB")}</span>
            </div>
          )}
          {/* Grand total row */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              paddingTop: 8,
              marginTop: 6,
              borderTop: "2px solid var(--color-sticker-yellow)",
            }}
          >
            <div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,.65)" }}>
                {tOrder("summary.to_pay_label")}
              </div>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,.5)", fontFamily: "monospace" }}>
                {tOrder("summary.to_pay_hint")}
              </div>
            </div>
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 22,
                fontWeight: 900,
                color: "var(--color-sticker-yellow)",
              }}
            >
              {preOrder
                ? formatPrice(totalFeeKopecks, "ru-RU", "RUB")
                : "—"}
            </div>
          </div>
        </div>

        {/* Error */}
        {submitError && (
          <div
            style={{
              padding: "10px 14px",
              background: "#FFF0EB",
              border: "1px solid var(--color-cta)",
              borderRadius: 8,
              fontSize: 11,
              color: "var(--color-cta)",
            }}
          >
            {submitError}
          </div>
        )}
      </div>

      {/* Submit CTA */}
      <TgMain
        disabled={
          submitMutation.isPending ||
          !preOrder ||
          selectedIds.length === 0
        }
        onClick={handleSubmit}
      >
        {submitMutation.isPending
          ? tOrder("summary.submitting")
          : tOrder("summary.submit", {
              amount: preOrder
                ? formatPrice(totalFeeKopecks, "ru-RU", "RUB")
                : "…",
            })}
      </TgMain>
    </TgPage>
  );
}
